import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@repo/error';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Alert, AlertDescription } from '@repo/ui/alert';
import { Button } from '@repo/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@repo/ui/form';
import { downloadFile } from '@repo/ui/lib/download-file';
import { PinInput, PinInputGroup, PinInputSlot } from '@repo/ui/pin-input';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { authClient } from '@/providers/session';
import { RecoveryCodeList } from './recovery-code-list';

export const ZViewRecoveryCodesForm = z.object({
  token: z.string().min(1, {
    get message() {
      return t('auth.tokenIsRequired');
    },
  }),
});

export type TViewRecoveryCodesForm = z.infer<typeof ZViewRecoveryCodesForm>;

export const ViewRecoveryCodesDialog = () => {
  useLocale();

  const [isOpen, setIsOpen] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<TViewRecoveryCodesForm>({
    defaultValues: {
      token: '',
    },
    resolver: zodResolver(ZViewRecoveryCodesForm, { error: validationError }),
  });

  const onFormSubmit = async ({ token }: TViewRecoveryCodesForm) => {
    setError(null);

    try {
      const data = await authClient.twoFactor.viewRecoveryCodes({
        token,
      });

      setRecoveryCodes(data.backupCodes);
    } catch (err) {
      const error = AppError.parseError(err);

      setError(error.code);
    }
  };

  const downloadRecoveryCodes = () => {
    if (recoveryCodes) {
      const blob = new Blob([recoveryCodes.join('\n')], {
        type: 'text/plain',
      });

      downloadFile({
        filename: 'raven-2FA-recovery-codes.txt',
        data: blob,
      });
    }
  };

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger asChild>
        <Button className="shrink-0">{t('auth.viewCodes')}</Button>
      </DialogTrigger>
      <DialogContent className="w-full max-w-xl md:max-w-xl lg:max-w-xl">
        {recoveryCodes ? (
          <div>
            <DialogHeader className="mb-4">
              <DialogTitle>{t('auth.viewRecoveryCodes')}</DialogTitle>
              <DialogDescription>
                {t('auth.yourRecoveryCodesAreListedBelowPleaseStoreThem')}{' '}
              </DialogDescription>
            </DialogHeader>
            <RecoveryCodeList recoveryCodes={recoveryCodes} />
            <DialogFooter className="mt-4">
              <DialogClose asChild>
                <Button variant="secondary">{t('auth.close')}</Button>
              </DialogClose>
              <Button onClick={downloadRecoveryCodes}>{t('auth.download')}</Button>
            </DialogFooter>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onFormSubmit)}>
              <DialogHeader className="mb-4">
                <DialogTitle>{t('auth.viewRecoveryCodes')}</DialogTitle>
                <DialogDescription>
                  {t('auth.pleaseProvideATokenFromYourAuthenticatorOrA')}{' '}
                </DialogDescription>
              </DialogHeader>
              <fieldset className="flex flex-col space-y-4" disabled={form.formState.isSubmitting}>
                <FormField
                  control={form.control}
                  name="token"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PinInput {...field} maxLength={6} value={field.value ?? ''}>
                          {Array(6)
                            .fill(null)
                            .map((_, i) => (
                              <PinInputGroup key={i}>
                                <PinInputSlot index={i} />
                              </PinInputGroup>
                            ))}
                        </PinInput>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {match(AppError.parseError(error).message)
                        .with('INCORRECT_TWO_FACTOR_CODE', () =>
                          t('auth.invalidCodePleaseTryAgain'),
                        )
                        .otherwise(() =>
                          t('workspace.somethingWentWrongPleaseTryAgainOrContactSupport'),
                        )}
                    </AlertDescription>
                  </Alert>
                )}
                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="secondary">
                      {t('reports.cancel')}{' '}
                    </Button>
                  </DialogClose>

                  <Button loading={form.formState.isSubmitting} type="submit">
                    {t('auth.view')}{' '}
                  </Button>
                </DialogFooter>
              </fieldset>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
